import { useState, useEffect, ChangeEvent, useMemo } from "react";
import ICatalog_Item from "@models/ICatalog_Item";
import IOrder from "@models/IOrder";
import IGroupItem from "@models/IGroupItem";
import IGroupSubsection from "@models/IGroupSubsection";
import { IAddBulkItems } from "@models/IAddBulkItems";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import UIService from "@services/UIService";
import { sortBy } from "lodash-es";
import { IItemsToProcess, getGroupItemData, renderGroupItemInventory } from "../productGroupViewerUtils";
import "./MultiProductGroupViewer.scss";

type Props = {
    orderModel: IOrder;
    productGroupModel: ICatalog_Item;
    addBulkItems: (items: IAddBulkItems[]) => void;
    hideTitle?: boolean;
    noInputPadding?: boolean;
    clearOnAdd?: boolean;
    orientation?: "horizontal" | "vertical";
};

const getSectionKey = (section: IGroupSubsection) => section.guid ?? (section.id !== undefined ? String(section.id) : section.title);

export const MultiProductGroupViewer = ({ productGroupModel, addBulkItems, noInputPadding, clearOnAdd, orientation }: Props) => {
    const isHorizontal = useMemo(() => orientation !== "vertical", [orientation]);
    const isSelectSection = productGroupModel.product_group_display_style === "SELECT_SECTION";
    const sortedSections = useMemo(() => sortBy(productGroupModel.multi_group_items || [], ["sequence"]), [productGroupModel]);

    // Build a flat IItemsToProcess across all subsection leaves, keyed by leaf guid
    const buildItemsToProcess = (): IItemsToProcess => {
        const result: IItemsToProcess = {};
        for (const section of productGroupModel.multi_group_items || []) {
            for (const leaf of section.items || []) {
                if (leaf.guid && leaf.id) {
                    result[leaf.guid] = { item_guid: leaf.guid, item_id: leaf.id, qty: "" };
                }
            }
        }
        return result;
    };

    const [itemsToProcess, setItemsToProcess] = useState<IItemsToProcess>(buildItemsToProcess);
    const [selectedSectionKey, setSelectedSectionKey] = useState<string>(() => {
        const first = sortedSections.find((s) => (s.items || []).some((l) => (l as IGroupItem).active));
        return first ? getSectionKey(first) : "";
    });

    useEffect(() => {
        setItemsToProcess(buildItemsToProcess());
        if (isSelectSection) {
            const first = sortBy(productGroupModel.multi_group_items || [], ["sequence"]).find((s) =>
                (s.items || []).some((l) => (l as IGroupItem).active)
            );
            setSelectedSectionKey(first ? getSectionKey(first) : "");
        }
    }, [productGroupModel]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>, guid: string) => {
        let value = e.target.value;
        const leaf = findLeaf(guid);

        if (leaf?.has_inventory && !leaf.allow_backorder) {
            if (Number(value) > (leaf.qty_on_hand || 0)) {
                value = (leaf.qty_on_hand || 0).toString();
            }
        }

        if (Number(value) < 0) value = "0";

        setItemsToProcess((prev) => ({
            ...prev,
            [guid]: { ...prev[guid], qty: value },
        }));
    };

    const findLeaf = (guid: string): IGroupItem | undefined => {
        for (const section of productGroupModel.multi_group_items || []) {
            const leaf = (section.items || []).find((l) => l.guid === guid);
            if (leaf) return leaf as IGroupItem;
        }
        return undefined;
    };

    const addItems = () => {
        const itemsToAdd: IAddBulkItems[] = [];

        for (const section of productGroupModel.multi_group_items || []) {
            for (const leaf of section.items || []) {
                const entry = leaf.guid ? itemsToProcess[leaf.guid] : undefined;
                if (entry && Number(entry.qty) > 0) {
                    itemsToAdd.push({
                        item: {
                            Catalog_Item: leaf as ICatalog_Item,
                            catalog_item_id: leaf.id!,
                            item_no: 0,
                            qty: Number(entry.qty),
                        },
                        kitDetails: [],
                        kitBuilderData: undefined,
                        itemOptionValues: [],
                    });
                }
            }
        }

        addBulkItems(itemsToAdd);

        if (clearOnAdd) {
            setItemsToProcess(buildItemsToProcess());
        }
    };

    const renderQtyInput = (leaf: IGroupItem) => {
        const { qtyInputClass, qty, disableQtyInput, max, guid } = getGroupItemData(leaf, {
            noInputPadding,
            isHorizontal,
            itemsToProcess,
        });

        return (
            <OwInputWrapper
                className={"text-center no-border-radius " + qtyInputClass}
                name={"qty"}
                type={"number"}
                fn={(e: any) => handleChange(e, guid)}
                max={max}
                value={qty}
                disabled={disableQtyInput}
                onWheelFn={UIService.stopScrollWheel}
            />
        );
    };

    const tableClass = "table table-sm " + (UIService.isMainViewLight() ? "table-light" : "table-dark");

    const renderSectionHorizontal = (leaves: IGroupItem[]) => (
        <table className={tableClass}>
            <thead>
                <tr className={"text-center"}>
                    {leaves
                        .filter((l) => l.active)
                        .map((l) => (
                            <th key={l.guid}>{l.group_title}</th>
                        ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {leaves
                        .filter((l) => l.active)
                        .map((l) => (
                            <td key={l.guid}>{renderQtyInput(l)}</td>
                        ))}
                </tr>
                <tr>
                    {leaves
                        .filter((l) => l.active)
                        .map((l) => (
                            <td key={l.guid}>{renderGroupItemInventory(l)}</td>
                        ))}
                </tr>
            </tbody>
        </table>
    );

    const renderSectionVertical = (leaves: IGroupItem[]) => (
        <table className={tableClass}>
            <tbody>
                {leaves
                    .filter((l) => l.active)
                    .map((l) => (
                        <tr key={l.guid}>
                            <th>{l.group_title}</th>
                            <td>{renderQtyInput(l)}</td>
                            <td>{renderGroupItemInventory(l)}</td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );

    const activeSections = sortedSections.filter((s) => (s.items || []).some((l) => (l as IGroupItem).active));
    const visibleSections = isSelectSection ? activeSections.filter((s) => getSectionKey(s) === selectedSectionKey) : activeSections;

    return (
        <div className={"MultiProductGroupViewer w-100"}>
            {isSelectSection && activeSections.length > 0 && (
                <div className={"d-flex flex-wrap gap-2 mb-3"}>
                    {activeSections.map((section) => {
                        const key = getSectionKey(section);
                        const isSelected = key === selectedSectionKey;
                        return (
                            <button
                                key={key}
                                type="button"
                                className={"btn btn-sm " + (isSelected ? "btn-primary" : "btn-outline-secondary")}
                                onClick={() => setSelectedSectionKey(key)}>
                                {section.title}
                            </button>
                        );
                    })}
                </div>
            )}
            {visibleSections.map((section) => {
                const leaves = sortBy((section.items || []) as IGroupItem[], ["group_sequence"]);

                return (
                    <div key={getSectionKey(section)} className={"mb-3"}>
                        {!isSelectSection && <strong className={"d-block mb-1"}>{section.title}</strong>}
                        {isHorizontal ? renderSectionHorizontal(leaves) : renderSectionVertical(leaves)}
                    </div>
                );
            })}

            <div className={"col-auto"}>
                <OwButton
                    className={"btn-primary"}
                    fn={addItems}
                    title={"add"}
                    leftIconClass={"far fa-plus"}
                    disabled={Object.keys(itemsToProcess).length === 0}
                />
            </div>
        </div>
    );
};
